/**
 * Emit one area_visit per navigation, from wherever the user actually is.
 *
 * This lived inside AppShell, which meant the two surfaces that do NOT render
 * AppShell emitted nothing at all: the launcher at `/` and every Administration
 * page under `/admin` (they render the shell's own ShellLayout). The admin
 * activity page was therefore blind to the pages an admin spends their time on,
 * itself included. Extracted here so both chromes can call it (ADO #1558).
 *
 * The matching rules are pure and live in ../model/areaMatch.
 */

import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { userActivityTracker } from './tracker';
import { areaForLocation, type AreaNavEntry } from '../model/areaMatch';

/** Beacon an area_visit whenever the route changes. Throttled in the tracker. */
export function useAreaTracking(navItems: readonly AreaNavEntry[] = []) {
  const { pathname } = useLocation();
  // Most modules build their nav array inline, so its identity changes on every
  // render; key the effect on the content instead of the reference.
  const navKey = navItems.map((n) => `${n.path ?? ''}>${n.label}`).join('|');
  const area = useMemo(
    () => areaForLocation(pathname, navItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, navKey],
  );
  useEffect(() => {
    userActivityTracker.trackAreaVisit(area);
  }, [area, pathname]);
}
